import { useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useGetPublicationQuery, type Manifest, type PublicationSelection } from '../../api/authoring'
import { ComponentName } from '../../ui/ComponentName'
import { Loading, QueryError } from '../../ui/QueryState'

/**
 * What one release holds: the site, and everything it was published
 * with.
 *
 * The list of releases said when each was made and nothing about what
 * was in it, so "which release had the old opening hours" had no answer
 * short of making each one live. The platform has always kept the
 * answer — a release is a selection of revisions — and no screen asked.
 *
 * **Two lists, because they behave differently.** What the site follows
 * was captured as it stood when the release was made. What is held at an
 * earlier version was held there on purpose, and stays there in every
 * release until somebody moves it.
 */
export function ReleaseContents({
  tenant,
  site,
  release,
  manifest,
  onClose,
}: {
  tenant: string
  site: string
  release: number | undefined
  manifest: Manifest | undefined
  onClose: () => void
}) {
  const read = useGetPublicationQuery(
    { tenant, website: site, publication: release ?? 0 },
    { skip: release === undefined },
  )

  return (
    <Dialog open={release !== undefined} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>What release {release} contains</DialogTitle>
      <DialogContent dividers>
        {read.isLoading && <Loading label="Opening the release…" />}
        {read.error && <QueryError error={read.error} title="Could not open this release" />}
        {read.data && <Selection tenant={tenant} manifest={manifest} selection={read.data.selection} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function Selection({
  tenant,
  manifest,
  selection,
}: {
  tenant: string
  manifest: Manifest | undefined
  selection: PublicationSelection
}) {
  // The site leads the list, and is not listed again: the platform's
  // `followed` carries the Website too, at the same revision, so drawing
  // both named it twice and counted it twice.
  const captured = [selection.website, ...selection.followed.filter((entry) => entry.id !== selection.website.id)]
  // Each name is its own read, at the revision the release holds, so a
  // site with a few hundred photos would ask a few hundred questions to
  // draw a list nobody scrolls to the end of. The first screenful is
  // named and the rest waits to be asked for.
  const [all, setAll] = useState(false)
  const shown = all ? captured : captured.slice(0, FIRST)
  return (
    <Stack spacing={2.5}>
      <Stack spacing={1}>
        <Typography variant="subtitle2">As it stood when this was published</Typography>
        <Typography variant="caption" color="text.secondary">
          The site, its pages and everything on them. {captured.length} in all.
        </Typography>
        {shown.map((entry) => (
          <ComponentName key={entry.id} tenant={tenant} id={entry.id} revision={entry.revision} manifest={manifest} />
        ))}
        {captured.length > shown.length && (
          <Stack direction="row">
            <Button size="small" onClick={() => setAll(true)}>
              Show the other {captured.length - shown.length}
            </Button>
          </Stack>
        )}
      </Stack>
      {selection.pinned.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="subtitle2">Held at an earlier version</Typography>
          <Typography variant="caption" color="text.secondary">
            These were held where they were on purpose, so edits made to them since were not published.
          </Typography>
          {selection.pinned.map((entry) => (
            <ComponentName
              key={`${entry.id}@${entry.revision}`}
              tenant={tenant}
              id={entry.id}
              revision={entry.revision}
              manifest={manifest}
            />
          ))}
        </Stack>
      )}
    </Stack>
  )
}

const FIRST = 40
